/* eslint-disable hooks/sort */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { FormattedMessage, useIntl } from "react-intl";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import clsx from "clsx";
import Breadcrumb from "@aleaplay/web-components/ui/Breadcrumb";
import {
  Content,
  Header,
  ImpersonatedUser as ImpersonatedMember,
  Layout,
  Menu,
  Sidebar,
  User,
} from "@aleaplay/web-components/ui/Layout";

import packageJSON from "@/../package.json";
import logo from "@/v1/assets/images/aleaLogo.png";
import CurrencyReportModal from "@/v1/components/organisms/CurrencyReportModal";
import GameLinkModal from "@/v1/components/organisms/GameLinkModal";
import LocaleSelector from "@/v1/components/organisms/LocaleSelector";
import { SESSION_TTL } from "@/v1/config/constants";
import menuOptions from "@/v1/config/menu";
import { changeLocale } from "@/v1/store/app/actions";
import { IS_PRODUCTION } from "@/v1/utils/constants";
import { FormModalProvider } from "@/v2/context/FormModalContext/FormModalProvider";
import { ModalProvider } from "@/v2/context/ModalContext/ModalProvider";
import { useCookie } from "@/v2/hooks/useCookie";
import { useIsInIframe } from "@/v2/hooks/useIsInIframe";
import { useUrlCommunicatorV2 } from "@/v2/hooks/useUrlCommunicatorV2";
import { useUserStore } from "@/v2/store/useUserStore";
import { IUser } from "@/v2/types/Auth/IUser.interface";
import { isEmpty } from "@/v2/utils/isEmpty";

import { Flex } from "../../../../v1/components/atoms/Flex/Flex";
import { RequestsManager } from "../../watchers/RequestsManager/RequestsManager";
import TaskManagerWidget from "../TaskManagerWidget/TaskManagerWidget";
import { TestNewsletterModal } from "../TestNewsletterModal/TestNewsletterModal";

import "./aleaLayout.scss";

/**
 * AleaLayout
 * Basic layout for alea CMS WEB
 */

interface AleaLayoutProps {
  children: React.ReactNode;
}

interface IMenuItem {
  icon: React.ReactNode;
  key: string;
  path: string;
  title: string;
}

// eslint-disable-next-line max-lines-per-function
export const AleaLayout: FC<AleaLayoutProps> = ({ children }) => {
  const dispatch = useDispatch();
  const crumbs = useSelector((state: RootStateOrAny) => state.app.breadcrumbs);
  const { pathname } = useLocation();
  const intl = useIntl();
  const { getCookie } = useCookie();
  const [embeddedGameVisibility, setEmbeddedGameVisibility] = useState(false);
  const [currencyReportVisibility, setCurrencyReportVisibility] = useState(false);
  const [testNewsletterVisibility, setTestNewsletterVisibility] = useState(false);
  const appVersion = packageJSON.version;
  const { push } = useHistory();

  const user = useUserStore((state) => state.user?.member);
  const userPermissions = useUserStore((state) => state.user?.permissions);
  const impersonatedUser = useUserStore((state) => state.impersonatedUser?.member);
  const removeImpersonatedUser = useUserStore((state) => state.removeImpersonatedUser);
  const logout = useUserStore((state) => state.logout);
  const isAdmin = useUserStore((state) => state.isAdmin);
  const isInIframe = useIsInIframe();
  const cookieLocale = getCookie("locale");

  useUrlCommunicatorV2();

  useEffect(() => {
    if (isInIframe && cookieLocale) {
      const actualLocale = cookieLocale === "en" ? "en-GB" : "es-ES";
      changeLocale(actualLocale)(dispatch);
    }
  }, [isInIframe, cookieLocale]);

  useIdleTimer({
    timeout: 1000 * 60 * Number(SESSION_TTL),
    onIdle: logout,
    debounce: 500,
  });

  const isActive = (option: any) => {
    if (option.isActive && typeof option.isActive === "function") {
      return option.isActive(pathname);
    }
    return pathname.startsWith(option.path);
  };

  const onClickItem = (item: IMenuItem, event: MouseEvent<HTMLButtonElement>) => {
    const visibilitySetters: { [key: string]: React.Dispatch<React.SetStateAction<boolean>> } = {
      "tools.game-link": setEmbeddedGameVisibility,
      "invoicing.currency-report": setCurrencyReportVisibility,
      "tools.test-newsletter": setTestNewsletterVisibility,
    };
    const setVisibility = visibilitySetters[item.key];
    if (setVisibility) {
      event.preventDefault();
      setVisibility(true);
    }
  };

  const filterOptionsByRole = useCallback(
    (options: any) => {
      return options.filter((option: any) => {
        switch (true) {
        case impersonatedUser &&
            !isEmpty<IUser>(impersonatedUser) &&
            option.adminRestricted &&
            !impersonatedUser.isAdmin:
        case option.adminRestricted && !isAdmin:
          return false;
        default:
          return true;
        }
      });
    },
    [user, impersonatedUser],
  );

  if (!user) return null;

  return (
    <Layout
      key={userPermissions?.join("")}
      className={clsx(`ui-layout--${process.env.REACT_APP_SCOPE?.toLowerCase()}`, {
        "is-windows": navigator.platform.toLowerCase() === "win32", // NOSONAR
      })}
    >
      {!isInIframe && (
        <Sidebar>
          <Header
            icon={<img src={logo} alt="Alea" className="ui-layout__logo" />}
            companyName="Alea"
            appVersion={`v${appVersion}`}
          />
          {menuOptions.map(({ title, key, options }: any) => (
            <Menu
              label={<FormattedMessage id={title} />}
              key={`${key}-${impersonatedUser || isAdmin}`}
            >
              {filterOptionsByRole(options).map((option: any) => (
                <Menu.Item
                  active={isActive(option)}
                  disabled={option.disabled}
                  icon={<option.icon />}
                  to={option.path}
                  key={option.key}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => onClickItem(option, event)}
                >
                  <FormattedMessage id={option.title} />
                </Menu.Item>
              ))}
            </Menu>
          ))}
          <User userName={`${user.firstName} ${user.lastName}`} onLogout={logout} />
          {impersonatedUser ? (
            <ImpersonatedMember
              userName={impersonatedUser?.firstName}
              removeImpersonatedUser={() => {
                push("/");
                removeImpersonatedUser();
              }}
            />
          ) : (
            <div />
          )}
        </Sidebar>
      )}
      <Content>
        <div className="ui-layout__helpers">
          {!IS_PRODUCTION && (
            <span
              className={`ui-layout__scope ui-layout__scope--${process.env.REACT_APP_SCOPE?.toLowerCase()}`}
            >
              {process.env.REACT_APP_SCOPE}
              <small>(v{packageJSON.version})</small>
            </span>
          )}

          {!isInIframe && (
            <Breadcrumb
              crumbs={[
                { id: 1, path: "/", title: intl.formatMessage({ id: "label.home" }) },
                ...crumbs,
              ]}
            />
          )}
          <LocaleSelector />
        </div>
        <FormModalProvider>
          <ModalProvider>
            {children}
            <GameLinkModal
              visible={embeddedGameVisibility}
              onClose={() => setEmbeddedGameVisibility(false)}
            />
            <CurrencyReportModal
              visible={currencyReportVisibility}
              onClose={() => setCurrencyReportVisibility(false)}
            />
            <TestNewsletterModal
              isVisible={testNewsletterVisibility}
              onClose={() => setTestNewsletterVisibility(false)}
            />
          </ModalProvider>
        </FormModalProvider>
        <Flex container>
          <RequestsManager />
          <TaskManagerWidget />
        </Flex>
      </Content>
    </Layout>
  );
};

export default AleaLayout;
